import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { sendAnalyticsEvents } from '../analytics/common';
import { setError } from '../features/error/errorSlice';
import { selectUserUuid, sendSignupTokenRequest } from '../features/signup/signupSlice';
import { selectLandingType } from '../features/plans/plansSlice';

export const useGoogleAuth = () => {
	const dispatch = useDispatch();
	const [isBtnPreloaded, setIsBtnPreloaded] = useState(false);
	const [googleScriptReady, setGoogleScriptReady] = useState(false);
  const userUuid = useSelector(selectUserUuid);
  const landingType = useSelector(selectLandingType);

	const handleGoogleAuth = useCallback(
		async (response) => {
			try {
				const token = response.credential;
				if (!token) {
					dispatch(
						setError({
							show: true,
							text: 'Failed to create an account using Google, please try again or use the default email method.',
						})
					);
					return;
				}
        await sendSignupTokenRequest({
          token,
          user_uuid: userUuid,
          landing_type: landingType,
        })(dispatch);
			} catch (error) {
				console.error('Google Authentication Error:', error);
				dispatch(
					setError({
						show: true,
						text: 'An unexpected error occurred during Google authentication. Please try again.',
					})
				);
			}
		},
		[dispatch, userUuid, landingType]
	);

	useEffect(() => {
		const checkGoogleScript = () => {
			if (window.google?.accounts?.id) {
				setGoogleScriptReady(true);
				return true;
			}
			return false;
		};

		if (checkGoogleScript()) return;

		const interval = setInterval(() => {
			if (checkGoogleScript()) {
				clearInterval(interval);
			}
		}, 100);

		return () => clearInterval(interval);
	}, []);

	useEffect(() => {
		const timer = setTimeout(() => {
			setIsBtnPreloaded(true);
		}, 1000);

		return () => clearTimeout(timer);
	}, []);

	useEffect(() => {
		if (isBtnPreloaded && googleScriptReady) {
			try {
				if (!process.env.REACT_APP_GOOGLE_SIGNUP_CLIENT_ID) {
					dispatch(
						setError({
							show: true,
							text: 'Google Sign-In is not properly configured. Please try again later.',
						})
					);
					return;
				}

				window.google.accounts.id.initialize({
					client_id: process.env.REACT_APP_GOOGLE_SIGNUP_CLIENT_ID,
					callback: handleGoogleAuth,
					ux_mode: 'popup',
					auto_select: false,
					cancel_on_tap_outside: true,
				});

				const initButton = () => {
					const buttonElement = document.getElementById('googleSignInButton');
					if (buttonElement) {
						window.google.accounts.id.renderButton(buttonElement, {
							type: 'standard',
							theme: 'outline',
							size: 'large',
							width: '343',
							text: 'continue_with',
							shape: 'rectangular',
							logo_alignment: 'left',
						});
					} else {
						setTimeout(initButton, 100);
					}
				};
				initButton();
			} catch (error) {
				dispatch(
					setError({
						show: true,
						text: 'Failed to initialize Google Sign-In. Please try again later.',
					})
				);
			}
		}
	}, [isBtnPreloaded, googleScriptReady, handleGoogleAuth, dispatch]);

	const handleCustomButtonClick = () => {
		if (!isBtnPreloaded) {
			dispatch(
				setError({
					show: true,
					text: 'Google Sign-In is not ready yet. Please wait a moment and try again.',
				})
			);
			return;
		}

		sendAnalyticsEvents('signup__click', {
      button__continue: 'google',
    });

		try {
			if (!window.google?.accounts?.id) {
				dispatch(
					setError({
						show: true,
						text: 'Google Sign-In is not available. Please try again later.',
					})
				);
				return;
			}
			window.google.accounts.id.prompt();
		} catch (error) {
			dispatch(
				setError({
					show: true,
					text: 'An error occurred while trying to sign in with Google. Please try again.',
				})
			);
		}
	};

	return {
		handleCustomButtonClick,
	};
};
